import { Linkedin, Mail, Globe } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import useSiteSettings from '@/hooks/useSiteSettings';

const FONT_HEADING = "'Cormorant Garamond', Cambria, Georgia, serif";
const FONT_BODY = "'Jost', sans-serif";

function LegalDisclaimerBar({ text }) {
  return (
    <div className="bg-[#0A0A0A] py-4 px-6 md:px-12" data-testid="legal-disclaimer-bar">
      <p className="max-w-7xl mx-auto text-[#666] text-[10px] leading-relaxed text-center" style={{ fontFamily: FONT_BODY }}>
        {text}
      </p>
    </div>
  );
}

export default function Footer({ s: sProp }) {
  const fetched = useSiteSettings();
  const s = sProp || fetched || {};
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashClick = (e, hash) => {
    e.preventDefault();
    const targetId = hash.replace('#', '');
    if (location.pathname === '/') {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/', { state: { scrollTo: targetId } });
    }
  };

  const brand = s.brand_name || 'SÀWAI CAPITAL';
  const tagline = s.footer_tagline || "India's first private, invitation-only deal discovery and transaction network for accredited capital allocators.";
  const legalTop = s.footer_legal_top || 'This platform is for qualified investors only. Nothing on this website constitutes investment advice or a solicitation to invest. All return figures are targets only and are not guaranteed. Investments in private markets carry significant risk including possible loss of capital. Sàwai Capital Dealroom is operated by Sawaicap Ventures Private Limited.';
  const legalBottom = s.footer_legal_bottom || 'This platform is for accredited investors only. All deal information is confidential. Past performance does not guarantee future returns. Investment in private markets involves risk of loss.';
  const copyrightText = s.footer_copyright_prefix || 'Sàwai Capital. All rights reserved.';
  const platformHeading = s.footer_platform_heading || 'Platform';
  const connectHeading = s.footer_connect_heading || 'Connect';
  const navLabelAsset = s.nav_label_asset_classes || 'Asset Classes';
  const navLabelHow = s.nav_label_process || 'How It Works';
  const navLabelWho = s.nav_label_investors || 'Who We Serve';
  const cin = s.company_cin || 'U86909HR2025PTC127987';
  const gstin = s.company_gstin || '06ABPCS1423G1ZE';
  const address = s.company_address || 'Sawaicap Ventures Private Limited, 90 B, 91 Springboard, Sector 18, Gurgaon, Haryana 122015';
  const email = s.contact_email || 'dealroom@sawaicap.com';
  const website = s.website_url || 'sawaicap.com';
  const linkedin = s.linkedin_url || 'https://www.linkedin.com/company/sawaicapital';

  return (
    <>
      <LegalDisclaimerBar text={legalTop} />
      <footer data-testid="footer" className="bg-[#1A1A1A] border-t border-[#C9A96E]/15 py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-16">
            <div>
              <h3 className="text-[#A0743A] text-xl tracking-[0.15em] mb-6" style={{ fontFamily: FONT_HEADING }}>
                {brand}
              </h3>
              <p className="text-[#999] text-sm leading-relaxed mb-4" style={{ fontFamily: FONT_BODY }}>
                {tagline}
              </p>
              <p className="text-[#555] text-[11px] leading-relaxed" style={{ fontFamily: FONT_BODY }}>
                CIN: {cin} &middot; GSTIN: {gstin}<br />
                Registered Office: {address}
              </p>
            </div>

            <div>
              <h4 className="text-[#C9A96E] text-xs uppercase tracking-[0.2em] mb-6 font-semibold" style={{ fontFamily: FONT_BODY }}>{platformHeading}</h4>
              <div className="space-y-3" style={{ fontFamily: FONT_BODY }}>
                <a href="#asset-classes" onClick={(e) => handleHashClick(e, '#asset-classes')} className="block text-[#999] hover:text-[#FAF8F5] text-sm transition-colors">{navLabelAsset}</a>
                <a href="#how-it-works" onClick={(e) => handleHashClick(e, '#how-it-works')} className="block text-[#999] hover:text-[#FAF8F5] text-sm transition-colors">{navLabelHow}</a>
                <a href="#who-we-serve" onClick={(e) => handleHashClick(e, '#who-we-serve')} className="block text-[#999] hover:text-[#FAF8F5] text-sm transition-colors">{navLabelWho}</a>
                <a
                  href="/faq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-[#999] hover:text-[#FAF8F5] text-sm transition-colors"
                  data-testid="footer-faq-link"
                >
                  {s.footer_faq_label || 'FAQ'}
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-[#C9A96E] text-xs uppercase tracking-[0.2em] mb-6 font-semibold" style={{ fontFamily: FONT_BODY }}>{connectHeading}</h4>
              <div className="space-y-3" style={{ fontFamily: FONT_BODY }}>
                <a href={`mailto:${email}`} className="flex items-center gap-3 text-[#999] hover:text-[#FAF8F5] text-sm transition-colors" data-testid="footer-email">
                  <Mail size={16} strokeWidth={1.25} className="text-[#A0743A]" />
                  {email}
                </a>
                <a href={`https://${website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#999] hover:text-[#FAF8F5] text-sm transition-colors" data-testid="footer-website">
                  <Globe size={16} strokeWidth={1.25} className="text-[#A0743A]" />
                  {website}
                </a>
                <a href={linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#999] hover:text-[#FAF8F5] text-sm transition-colors" data-testid="footer-linkedin">
                  <Linkedin size={16} strokeWidth={1.25} className="text-[#A0743A]" />
                  LinkedIn
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-[#C9A96E]/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-col md:flex-row items-center gap-3 md:gap-6">
              <p className="text-[#555] text-xs" style={{ fontFamily: FONT_BODY }}>
                &copy; {new Date().getFullYear()} {copyrightText}
              </p>
              <div className="flex items-center gap-4 text-xs" style={{ fontFamily: FONT_BODY }}>
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#777] hover:text-[#C9A96E] transition-colors" data-testid="footer-terms-link">{s.footer_terms_label || 'Terms & Conditions'}</a>
                <span className="text-[#444]">|</span>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#777] hover:text-[#C9A96E] transition-colors" data-testid="footer-privacy-link">{s.footer_privacy_label || 'Privacy & Cookie Policy'}</a>
              </div>
            </div>
            <p className="text-[#555] text-xs leading-relaxed max-w-xl text-center md:text-right" style={{ fontFamily: FONT_BODY }}>
              {legalBottom}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
}
